// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * Shared-record rendezvous + poll-to-terminal (ADR-002 P1-P6), driven over the
 * Gateway transport. Adapted from the sibling AU0008 updater widget.
 *
 * The AU0009 widget is the CLIENT/requesting side of the data-bridge (P1): it
 * writes a request into a db_service_request_bridge record and polls that same
 * record until it reaches a terminal state (P4). The server-side
 * ProcessServiceRequest workflow (#15) reacts to the transition into "Pending"
 * (P2), resolves the reference against the authoritative capability server-side
 * (P6), writes the response back into the same record (P1/P3), and drives it to
 * Success / Error (P5).
 *
 * Contract mirrored from `main`:
 *   form   : db_service_request_bridge  (src/data-models/db_service_request_bridge.ds)
 *   kinds  : GetDeviceBySerialNumber | GetDeviceBySoftwareId
 *          | GetAU0004LatestAccountability | GetAU0008LatestAccountability
 *          | GetFirmwarePackageMetaByReferenceId
 *          | GetAU0001LatestAttributionByDeviceAnchor
 *          | ListFirmwarePackageMeta
 *   states : Idle | Pending | Success | Error
 *   control plane : request_type, status, correlation_id, first_error
 *   data plane    : request_payload, response_payload (attached content)
 *
 * READ-ONLY (ADR-002 / README): AU0009 writes nothing to any foreign system.
 * There is deliberately NO WriteAccountability-style kind here (unlike AU0008).
 */

import type { GatewayClient } from "./gateway-client";
import type { InterpretedResponse } from "./envelope";
import type { BridgeError } from "./errors";
import { makeBridgeError } from "./errors";
import type { BridgeRequestType, BridgeStatus } from "./types";
import { isTerminal } from "./types";

/**
 * The allow-listed ZOHO.CREATOR operations this widget asks the Gateway to relay.
 * Only what the rendezvous needs: add the request record, upload the request
 * payload, promote to Pending, poll by id, and read the attached response file.
 * Concrete module/function spellings match the Widget JS API surface the Gateway
 * invokes as ZOHO.CREATOR[module][function](config).
 */
export const BRIDGE_CALLS = {
  addRecord: { module: "API", function: "addRecord" },
  uploadFile: { module: "API", function: "uploadFile" },
  updateRecord: { module: "API", function: "updateRecord" },
  getRecord: { module: "API", function: "getRecordById" },
  readFile: { module: "API", function: "readFile" },
} as const;

/** The bound form + report link-names on the Zoho Creator app. */
// TODO(impl): confirm the report link-name spelling on the AU0009 app.
export const BRIDGE_FORM = "db_service_request_bridge";
export const BRIDGE_REPORT = "db_service_request_bridge_Report";

/** Poll parameters (ADR-002 P4 bounded give-up). All injectable for tests. */
export interface PollParams {
  /** Delay between poll reads, ms. */
  readonly intervalMs: number;
  /** Maximum number of poll reads before giving up (bounded). */
  readonly maxAttempts: number;
  /** Async sleep (injected; real timers in production, fake in tests). */
  sleep(ms: number): Promise<void>;
}

/**
 * The result of one full rendezvous exchange. Discriminated on `ok`; on success
 * the caller receives the decoded response_payload document. Never infers
 * outcome from payload shape (ADR-002 P5).
 */
export type ExchangeResult =
  | { readonly ok: true; readonly responsePayload: unknown }
  | { readonly ok: false; readonly error: BridgeError };

/** A request record as written to the bridge form (ADR-002 P3 planes). */
interface BridgeRecordRequest {
  readonly request_type: BridgeRequestType;
  readonly status: "Idle" | "Pending";
  readonly correlation_id: string;
}

/** Read a BridgeStatus from an opaque poll result, or null if unreadable. */
function readState(result: unknown): BridgeStatus | null {
  const states: readonly string[] = ["Idle", "Pending", "Success", "Error"];
  if (typeof result !== "object" || result === null) return null;
  const rec = result as Record<string, unknown>;

  const directStatus = rec["status"];
  if (typeof directStatus === "string" && states.includes(directStatus)) {
    return directStatus as BridgeStatus;
  }

  const message = rec["message"];
  if (typeof message === "object" && message !== null) {
    const messageData = (message as Record<string, unknown>)["data"];
    if (typeof messageData === "object" && messageData !== null) {
      const nestedStatus = (messageData as Record<string, unknown>)["status"];
      if (typeof nestedStatus === "string" && states.includes(nestedStatus)) {
        return nestedStatus as BridgeStatus;
      }
    }
  }
  return null;
}

/** Read first_error (control plane) from an Error record, sanitised to string. */
function readFirstError(result: unknown): string | undefined {
  if (typeof result !== "object" || result === null) return undefined;
  const rec = result as Record<string, unknown>;
  const directError = rec["first_error"];
  if (typeof directError === "string" && directError.length > 0) return directError;

  const message = rec["message"];
  if (typeof message === "object" && message !== null) {
    const messageData = (message as Record<string, unknown>)["data"];
    if (typeof messageData === "object" && messageData !== null) {
      const nestedError = (messageData as Record<string, unknown>)["first_error"];
      return typeof nestedError === "string" && nestedError.length > 0 ? nestedError : undefined;
    }
  }
  return undefined;
}

/**
 * A small, explicit set of fields ADR-008 D2 places as SIBLINGS of `data` in
 * the device-resolution response envelope (`{ success, data, is_demo }`)
 * rather than nested inside it — unlike every other request kind's payload,
 * where the unwrapped `data` content is everything a mapper needs (see
 * au0001-response-mapper.ts / firmware-delivery-*-mapper.ts, which all rely
 * on this function's one-level `data` unwrap handing them that inner object
 * directly). Diagnosed on #57: `readFileObject` returning `data` alone
 * silently dropped a request-kind-specific sibling field, so
 * device-registry-mapper.ts never saw `is_demo` at all — a genuine identity
 * value the wire carries, not a type/format mismatch. Named here, not
 * silently assumed (ISO/IEC 5055): only these keys are carried; nothing else
 * at the envelope level leaks into `data`.
 */
const CARRIED_ENVELOPE_SIBLINGS = ["is_demo"] as const;

/**
 * Merge any `CARRIED_ENVELOPE_SIBLINGS` present on `envelope` onto `data`,
 * without mutating `data`. Returns `data` itself, unchanged, when none of
 * those keys are present — zero behaviour change for the six request kinds
 * that carry no such sibling.
 */
function withCarriedEnvelopeSiblings(
  data: Record<string, unknown>,
  envelope: Record<string, unknown>,
): Record<string, unknown> {
  let merged: Record<string, unknown> | undefined;
  for (const key of CARRIED_ENVELOPE_SIBLINGS) {
    if (key in envelope) {
      merged ??= { ...data };
      merged[key] = envelope[key];
    }
  }
  return merged ?? data;
}

/**
 * Recover the already-decoded response_payload JSON object from a readFile reply,
 * or null if none is present. The readFile relay returns the content already
 * parsed under `message.data`; it is NOT a raw string, so no JSON.parse is done.
 *
 * An explicitly-null `data` field (e.g. `{"success":true,"data":null}`) is a
 * valid response shape — the AU responded but has no record for this device —
 * and is returned as-is so the caller can interpret it. Only a truly
 * absent/unreadable shape yields null (malformed_message).
 */
function readFileObject(result: unknown): Record<string, unknown> | null {
  if (typeof result !== "object" || result === null) return null;
  const rec = result as Record<string, unknown>;

  const message = rec["message"];
  if (typeof message === "object" && message !== null) {
    const envelope = message as Record<string, unknown>;
    const data = envelope["data"];
    if (typeof data === "object" && data !== null) {
      return withCarriedEnvelopeSiblings(data as Record<string, unknown>, envelope);
    }
    // data is explicitly null → the message object IS the payload envelope.
    if (data === null) return envelope;
  }

  const directData = rec["data"];
  if (typeof directData === "object" && directData !== null) {
    return withCarriedEnvelopeSiblings(directData as Record<string, unknown>, rec);
  }
  // data is explicitly null → the top-level object IS the payload envelope.
  if (directData === null && typeof rec["success"] !== "undefined") return rec;

  return null;
}

/**
 * Fetch the attached response_payload document on the Success terminal state
 * (ADR-002 P3 data plane). response_payload is an upload-file field on
 * db_service_request_bridge, so its content is fetched with the allow-listed
 * readFile relay against the same record id; readFile returns the content
 * already decoded under `message.data`.
 *
 * An explicitly-null data payload (e.g. an Updater AU that responded but has
 * no operation record for this device) is a VALID response — the caller's
 * mapper decides how to interpret it. Only a truly missing/unreadable document
 * yields a malformed_message error (ADR-002 P5, no silent fallback).
 */
async function fetchResponsePayloadJson(
  client: GatewayClient,
  recordId: string,
): Promise<ExchangeResult> {
  const read: InterpretedResponse = await client.call({
    module: BRIDGE_CALLS.readFile.module,
    function: BRIDGE_CALLS.readFile.function,
    config: { reportName: BRIDGE_REPORT, fieldName: "response_payload", id: recordId },
  });
  if (!read.ok) return { ok: false, error: read.error };

  const payload = readFileObject(read.result);
  if (payload === null) {
    return {
      ok: false,
      error: makeBridgeError(
        "malformed_message",
        "The response_payload readFile reply did not carry a decoded content object.",
      ),
    };
  }
  return { ok: true, responsePayload: payload };
}

/**
 * Perform one rendezvous exchange: add the request record, upload the request
 * payload, promote to Pending, then poll the same record to a terminal state
 * with a bounded give-up (ADR-002 P1/P2/P4/P5).
 *
 * @param client         the Gateway transport.
 * @param kind           the request kind written to the shared record.
 * @param correlationId  the client-generated correlation id (ADR-007 D3).
 * @param requestPayload the serialized request_payload (the reference inside).
 * @param poll           bounded poll parameters (ADR-002 P4).
 */
export async function runExchange(
  client: GatewayClient,
  kind: BridgeRequestType,
  correlationId: string,
  requestPayload: string,
  poll: PollParams,
): Promise<ExchangeResult> {
  // 1. Create the shared record in Idle state (ADR-002 P1/P2).
  const record: BridgeRecordRequest = {
    request_type: kind,
    status: "Idle",
    correlation_id: correlationId,
  };
  const added = await client.call({
    module: BRIDGE_CALLS.addRecord.module,
    function: BRIDGE_CALLS.addRecord.function,
    config: { formName: BRIDGE_FORM, data: { data: record } },
  });
  if (!added.ok) return { ok: false, error: added.error };

  const recordId = readAddedRecordId(added.result);
  if (recordId === null) {
    return {
      ok: false,
      error: makeBridgeError(
        "malformed_message",
        "The add-record reply did not carry a usable record id to poll.",
      ),
    };
  }

  // 2. Upload the request_payload into the file field (Zoho Creator JS API v1).
  const fileBlob = new Blob([requestPayload], { type: "application/json" });
  const file = new File([fileBlob], "request.json", { type: "application/json" });
  const uploaded = await client.call({
    module: BRIDGE_CALLS.uploadFile.module,
    function: BRIDGE_CALLS.uploadFile.function,
    config: { id: recordId, reportName: BRIDGE_REPORT, fieldName: "request_payload", file },
  });
  if (!uploaded.ok) return { ok: false, error: uploaded.error };

  // 3. Promote the record to Pending so the server-side workflow consumes it (P2).
  const promoted = await client.call({
    module: BRIDGE_CALLS.updateRecord.module,
    function: BRIDGE_CALLS.updateRecord.function,
    config: { id: recordId, reportName: BRIDGE_REPORT, data: { data: { status: "Pending" } } },
  });
  if (!promoted.ok) return { ok: false, error: promoted.error };

  // 4. Poll the same record to a terminal state (ADR-002 P4 bounded give-up).
  for (let attempt = 0; attempt < poll.maxAttempts; attempt++) {
    await poll.sleep(poll.intervalMs);
    const read: InterpretedResponse = await client.call({
      module: BRIDGE_CALLS.getRecord.module,
      function: BRIDGE_CALLS.getRecord.function,
      config: { reportName: BRIDGE_REPORT, id: recordId },
    });
    if (!read.ok) return { ok: false, error: read.error };

    const state = readState(read.result);
    if (state === null) continue; // unreadable this round; keep within bound
    if (!isTerminal(state)) continue; // Idle / Pending -> keep polling (P4)

    if (state === "Success") {
      // response_payload is an upload-file field (P3 data plane): fetch + decode.
      return fetchResponsePayloadJson(client, recordId);
    }
    // Error terminal state (P5): surface the server-side first_error.
    return {
      ok: false,
      error: makeBridgeError(
        "system_error",
        "The server-side resolver drove the request to the Error terminal state.",
        readFirstError(read.result),
      ),
    };
  }

  // Bounded give-up reached without a terminal state (ADR-002 P4).
  return {
    ok: false,
    error: makeBridgeError(
      "timeout",
      "The bridge record did not reach a terminal state within the bounded poll window.",
    ),
  };
}

/**
 * Extract the new record id from an ADD reply. The id field spelling can vary,
 * so a small, explicit set of candidates is checked (no silent guessing beyond
 * these).
 */
function readAddedRecordId(result: unknown): string | null {
  if (typeof result !== "object" || result === null) return null;
  const rec = result as Record<string, unknown>;

  const direct = rec["ID"] ?? rec["id"];
  if (typeof direct === "string" && direct.length > 0) return direct;
  if (typeof direct === "number") return String(direct);

  const data = rec["data"];
  if (typeof data === "object" && data !== null) {
    const nested = (data as Record<string, unknown>)["ID"] ?? (data as Record<string, unknown>)["id"];
    if (typeof nested === "string" && nested.length > 0) return nested;
    if (typeof nested === "number") return String(nested);
  }

  const message = rec["message"];
  if (typeof message === "object" && message !== null) {
    const messageData = (message as Record<string, unknown>)["data"];
    if (typeof messageData === "object" && messageData !== null) {
      const nestedInMessage =
        (messageData as Record<string, unknown>)["ID"] ?? (messageData as Record<string, unknown>)["id"];
      if (typeof nestedInMessage === "string" && nestedInMessage.length > 0) return nestedInMessage;
      if (typeof nestedInMessage === "number") return String(nestedInMessage);
    }
  }
  return null;
}
